import JsBarcode from "jsbarcode";
import { toast } from "sonner";

const svgFor = (code) => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  try {
    JsBarcode(svg, String(code), {
      format: "CODE128", width: 2, height: 46, displayValue: true,
      fontSize: 12, margin: 4, font: "Manrope, sans-serif",
    });
  } catch {
    return null;
  }
  return svg.outerHTML;
};

const esc = (s) => String(s ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
const rupees = (n) => `\u20b9${Number(n || 0).toLocaleString("en-IN")}`;
const model = (p) => p.model_no || p.sku || "\u2014";
const title = (p) => p.name || p.product_name || "";

const STYLES = `
  @page { size: A4; margin: 8mm; }
  body { font-family: Manrope, system-ui, sans-serif; margin: 0; padding: 0; color: #1f1a16; }
  h1 { font-size: 13px; letter-spacing: .12em; text-transform: uppercase; color: #8a7f74;
       margin: 0 0 8px; font-weight: 700; }
  .sheet { display: flex; flex-wrap: wrap; gap: 4mm; }
  .label { width: 60mm; box-sizing: border-box; border: 1px solid #d9cfc4; border-radius: 6px;
           padding: 7px 8px 5px; text-align: center; page-break-inside: avoid; break-inside: avoid; }
  .co { font-size: 7.5px; letter-spacing: .1em; text-transform: uppercase; color: #8a7f74; margin: 0 0 2px; }
  .nm { font-size: 10.5px; font-weight: 700; margin: 0; line-height: 1.2; min-height: 25px; }
  .pr { font-size: 10px; font-weight: 700; color: #a4520d; margin: 2px 0 3px; }
  svg { max-width: 100%; height: auto; display: block; margin: 0 auto; }
  .mdl { font-size: 9px; font-weight: 700; letter-spacing: .05em; margin: 2px 0 0; color: #3d332b; }
  .cat { font-size: 7.5px; color: #8a7f74; margin: 1px 0 0; }
  @media print { .label { border-color: #bbb; } h1 { display: none; } }
`;

const sheetHTML = (products, company, copies) => {
  const list = (Array.isArray(products) ? products : [products]).filter((p) => p?.barcode);
  const labels = [];
  list.forEach((p) => {
    const svg = svgFor(p.barcode);
    if (!svg) return;
    for (let i = 0; i < copies; i++) {
      labels.push(`<div class="label">
        <p class="co">${esc(company)}</p>
        <p class="nm">${esc(title(p))}</p>
        <p class="pr">${rupees(p.selling_price)}</p>
        ${svg}
        <p class="mdl">MODEL ${esc(model(p))}</p>
        ${p.category ? `<p class="cat">${esc(p.category)}</p>` : ""}
      </div>`);
    }
  });
  return {
    count: labels.length,
    html: `<!doctype html><html><head><meta charset="utf-8" />
      <title>${esc(company)} barcode stickers</title><style>${STYLES}</style></head>
      <body><h1>${esc(company)} \u00b7 ${labels.length} barcode sticker${labels.length > 1 ? "s" : ""}</h1>
      <div class="sheet">${labels.join("")}</div></body></html>`,
  };
};

/** Opens a print-ready A4 sheet of barcode stickers for the given products. */
export const printBarcodes = (products, company = "Megatronix Vision", copies = 1) => {
  const { count, html } = sheetHTML(products, company, copies);
  if (!count) return toast.error("These products do not have a barcode yet.");
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) return toast.error("Please allow pop-ups to print barcode stickers.");
  w.document.write(html.replace("</body>",
    `<script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script></body>`));
  w.document.close();
  toast.success(`${count} sticker${count > 1 ? "s" : ""} ready to print`);
};

/** Saves the same sticker sheet as a file that can be opened and printed later. */
export const downloadBarcodeSheet = (products, company = "Megatronix Vision", copies = 1) => {
  const { count, html } = sheetHTML(products, company, copies);
  if (!count) return toast.error("These products do not have a barcode yet.");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
  a.download = `barcode-stickers-${new Date().toISOString().slice(0, 10)}.html`;
  a.click();
  URL.revokeObjectURL(a.href);
  toast.success(`${count} sticker${count > 1 ? "s" : ""} downloaded \u00b7 open the file and print`);
};

/** Downloads a single barcode as an SVG file. */
export const downloadBarcode = (product) => {
  const svg = svgFor(product?.barcode);
  if (!svg) return toast.error("This product does not have a valid barcode yet.");
  const blob = new Blob([svg], { type: "image/svg+xml" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${product.sku || product.barcode}-barcode.svg`;
  a.click();
  URL.revokeObjectURL(a.href);
  toast.success("Barcode downloaded");
};
